import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { Button, Heading } from "@foris/avocado-suite";
import cx from "classnames";
import { AppContext } from "../../../context/EditSessionsContext";
import { Types } from "../../../context/formData.reducer";
import { Types as TypeSwitch } from "../../../context/switchPage.reducer";
import {
  ClassroomFilterInput,
  ClassroomFilterFieldsInput,
  ClassroomPage,
  Session,
} from "@models/ISchema";
import { IParams } from "@models/IParams";
import AdvanceTable from "../../../components/Classroom/AdvanceTable";
import AdvanceForm from "../../../components/Classroom/AdvanceForm";
import { classroomsAvailabilities } from "@services/collections/advance-search/graphql/classroomAdvanceSearch.query";
import { daysEn } from "@utils/days";
import css from "./advanceSearch.module.scss";
import { useTranslation } from "react-i18next";

const AdvanceSearch: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);
  const { scenario, origin }: IParams = useParams();
  const client = useApolloClient();
  const [data, setData] = useState<ClassroomPage>(null);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(null);
  const [campus, setCampus] = useState(null);
  const [classroomType, setClassroomType] = useState(null);
  const [minCapacity, setMinCapacity] = useState(null);
  const [maxCapacity, setMaxCapacity] = useState(null);
  const [hasEvents, setHasEvents] = useState(null);
  const [selected, setSelected] = useState(state?.form?.editedSessions?.classrooms);
  const [page, setPage] = useState(1);
  const size = 10;
  const selectedCreate = state?.form?.selectedCreateSession;

  const { t } = useTranslation();

  let codeSessions = [];
  if (!selectedCreate) {
    codeSessions = state?.form?.selectedSessions.map(value => value.id);
  } else {
    codeSessions = ["Nueva sesión"];
  }

  const getVariables = ({
    building,
    classroomType,
    minCapacity,
    maxCapacity,
    hasEvents,
    campus,
    _page = page,
  }: {
    building: string;
    classroomType: string;
    minCapacity: string;
    maxCapacity: string;
    hasEvents: boolean;
    campus?: string;
    _page: number;
  }) => {
    const intervalsCheck = state?.form?.editedSessions?.intervals?.filter(
      value => value.checked === true,
    );
    const availabilities = state?.form?.selectedSessions
      ?.map(_value => {
        const value = _value as Session;
        const intervals =
          intervalsCheck.length > 0
            ? intervalsCheck.map(value => parseInt(value.id))
            : value?.assignment?.intervals.map(value => parseInt(value.id));
        const dayEdited = state?.form?.editedSessions?.blocks?.day;
        const day = dayEdited ?? value?.assignment?.blockRange?.start?.day;
        if (day) {
          let dayNumber = daysEn.findIndex(value => value === day);
          dayNumber = dayNumber || dayNumber === 0 ? dayNumber + 1 : 0;
          const startEdited = state?.form?.editedSessions?.blocks?.startTime;
          const start = startEdited ?? value?.assignment?.blockRange?.start?.startingTime;
          const endEdited = state?.form?.editedSessions?.blocks?.endTime;
          const end = endEdited ?? value?.assignment?.blockRange?.end?.endingTime;
          return {
            dayId: [dayNumber?.toString()],
            blockRange: {
              start: start,
              end: end,
            },
            intervals,
          };
        }
        return null;
      })
      .filter(value => value !== null);
    const fieldsInputs: ClassroomFilterFieldsInput[] = [];
    if (classroomType)
      fieldsInputs.push({
        classroomTypeId: {
          eq: parseInt(classroomType),
        },
      });
    if (building)
      fieldsInputs.push({
        buildingId: {
          eq: parseInt(building),
        },
      });
    if (hasEvents)
      fieldsInputs.push({
        hasEvents,
      });

    if (minCapacity)
      fieldsInputs.push({
        capacity: {
          value: {
            gte: parseInt(minCapacity),
          },
        },
      });
    if (maxCapacity)
      fieldsInputs.push({
        capacity: {
          value: {
            lte: parseInt(maxCapacity),
          },
        },
      });
    if (campus) {
      fieldsInputs.push({
        building: {
          campusId: {
            eq: parseInt(campus),
          },
        },
      });
    }
    const filter: ClassroomFilterInput = {
      fields: {
        and: [...fieldsInputs, { availabilities }],
      },
      pagination: {
        page: _page,
        size,
      },
    };
    const variables = {
      originId: origin,
      scenarioId: scenario,
      filter,
    };
    return variables;
  };

  const handlerSearch = async (
    _building: string,
    _campus: string,
    _classroomType: string,
    _minCapacity: string,
    _maxCapacity: string,
    _hasEvents: boolean,
  ) => {
    setData(null);
    setBuilding(_building);
    setClassroomType(_classroomType);
    setMinCapacity(_minCapacity);
    setMaxCapacity(_maxCapacity);
    setHasEvents(_hasEvents);
    setCampus(_campus);
  };

  const classroomSearch = async (
    building: string,
    campus = "",
    classroomType: string,
    minCapacity: string,
    maxCapacity: string,
    hasEvents: boolean,
    _page = page,
  ) => {
    try {
      setLoading(true);
      const variables = getVariables({
        building,
        campus,
        classroomType,
        minCapacity,
        maxCapacity,
        hasEvents,
        _page,
      });

      const { data } = await client.query({ query: classroomsAvailabilities, variables });
      const dataQuery: ClassroomPage = data?.cube?.classroomsAvailabilities;
      setData(dataQuery);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <article className={css.advanceSearch}>
      <header className={css.advanceSearch_header}>
        <Heading className={css.headerTitle} type="h2">
          {t("assignation-edit.advanced-search.classroom.header.title")} -{" "}
          {codeSessions?.length === 1
            ? t("assignation-edit.advanced-search.classroom.header.session")
            : t("assignation-edit.advanced-search.classroom.header.sessions")}{" "}
          {codeSessions.join(" | ")}
        </Heading>
      </header>

      <AdvanceForm
        onCallback={(
          building: string,
          classroomType: string,
          minCapacity: string,
          maxCapacity: string,
          hasEvents: boolean,
          campus?: string,
        ) => {
          handlerSearch(building, campus, classroomType, minCapacity, maxCapacity, hasEvents);
          classroomSearch(building, campus, classroomType, minCapacity, maxCapacity, hasEvents);
        }}
        setPage={() => setPage(1)}
      />

      <section className={css.advanceSearch_content}>
        {!!data?.pageInfo && (
          <Heading className={css.contentTitle} type="h2">
            {t("assignation-edit.advanced-search.classroom.table-content.title", {
              count: data?.pageInfo?.total ?? 0,
            })}
          </Heading>
        )}

        <AdvanceTable
          loading={loading}
          data={data?.items}
          size={data?.pageInfo?.size}
          total={data?.pageInfo?.total}
          page={page}
          selected={selected}
          setSelected={setSelected}
          onPageChange={value => {
            setPage(value);
            classroomSearch(
              building,
              campus,
              classroomType,
              minCapacity,
              maxCapacity,
              hasEvents,
              value,
            );
          }}
        />
      </section>

      {data?.pageInfo?.total > 0 && (
        <footer className={cx(css.footer, "container-row")}>
          <Button
            variant="ghost"
            className={css.footer_btn}
            onClick={() => {
              dispatch({ type: TypeSwitch.FormPage, payload: true });
            }}
          >
            {t("assignation-edit.advanced-search.classroom.table-content.cancel")}
          </Button>
          <Button
            className={css.footer_btn}
            onClick={() => {
              dispatch({
                type: Types.ClassroomEditedSessions,
                payload: {
                  classrooms: selected,
                },
              });
              state?.form?.selectedSessions?.length &&
                dispatch({ type: Types.SavedSessions, payload: [state.form.editedSessions] });
              dispatch({ type: TypeSwitch.FormPage, payload: true });
            }}
          >
            {t("assignation-edit.advanced-search.classroom.table-content.save")}
          </Button>
        </footer>
      )}
    </article>
  );
};

export default AdvanceSearch;
